import get from 'lodash/get';


const GROUP_START = '(';
const GROUP_END = ')';
export const AND_OPERATOR = '&';
export const OR_OPERATOR = '|';


function toArray(rule) {
  const ruleLogic = rule.match(/(\d+|\||&|\(|\))/g);
  // Keep track of group at each level in dict with groupLevel key, groupArray value
  // nestedGroups[1] will contain entire nested array at finish
  const nestedGroups = { 0: [] };
  let groupLevel = 0;
  ruleLogic.forEach(value => {
    if (value === GROUP_START) {
      groupLevel += 1;
      nestedGroups[groupLevel] = [];
    } else if (value === GROUP_END) {
      groupLevel -= 1;
      nestedGroups[groupLevel].push(nestedGroups[groupLevel + 1]);
    } else if (isNaN(value)) {
      nestedGroups[groupLevel].push(value);
    } else {
      nestedGroups[groupLevel].push(+value);
    }
  });
  return nestedGroups[1];
}


function toString(rule) {
  const ruleString = rule.map(value => {
    if (Array.isArray(value)) {
      return toString(value);
    }
    return String(value);
  }).join('');

  return `(${ruleString})`;
}


function splitPosition(dotPosition) {
  const valuePositionArray = dotPosition.split('.');
  const valuePosition = +valuePositionArray.pop();
  const parentPosition = `[${valuePositionArray.join('][')}]`;
  return [parentPosition, valuePosition];
}


class Logician {
  constructor(rule) {
    if (rule) {
      if (Array.isArray(rule)) {
        // If the rule is an array, join the contents, wrap in parentheses
        this.rule = rule;
      } else if (typeof rule === 'string') {
        // Check for outer parentheses if rule is a string
        if (!rule.charAt(0) === '(' && !rule.charAt(rule.length - 1) === ')') {
          // Add parentheses if it doesn't have any
          this.rule = toArray(`(${rule})`);
        } else {
          this.rule = toArray(rule);
        }
      } else {
        throw new Error('Rule passed to Logician must be a string or Logician array.');
      }
    } else {
      this.rule = [];
    }
    // eslint-disable-next-line no-constructor-return
    return this;
  }


  addThreshold(position, thresholdId) {
    const [parent, groupPosition] = this.getGroupAndValuePosition(position);
    const group = typeof parent === 'undefined' ? this.rule : parent[groupPosition];
    if (group.length > 0) group.push(AND_OPERATOR);
    group.push(thresholdId);
    return this;
  }


  removeThreshold(thresholdId, ruleArray = this.rule) {
    const arrsToCheck = [];
    const idxToRemove = [];
    // TODO: Move backwards through these arrays to preserve ordering also
    ruleArray.forEach((value, idx, values) => {
      if (Array.isArray(value)) {
        this.removeThreshold(thresholdId, value);
        arrsToCheck.push(idx);
      } else if (!isNaN(value) && value === thresholdId) {
        if (idx === 0) {
          idxToRemove.push(idx);
          if (values.length > 1) idxToRemove.push(idx + 1);
        } else {
          idxToRemove.push(idx - 1); idxToRemove.push(idx);
        }
      }
    });
    arrsToCheck.forEach(idx => {
      if (ruleArray[idx].length === 1) [ruleArray[idx]] = ruleArray[idx];
    });
    idxToRemove.reverse().forEach(idx => { ruleArray.splice(idx, 1); });
    return this;
  }


  // Recurses through nested arrays, removes thresholdId from any other positions
  // inside this.rule and inserts it at the passed position.
  // Also checks for single-item arrays to flatten along the way and flattens them at end
  moveThreshold(position, thresholdPosition, thresholdId, ruleArray = this.rule) {
    let removeNextIndex = false;
    // Move backwards through all arrays to preserve index ordering upon insertion/deletion
    for (let idx = ruleArray.length - 1; idx >= 0; idx -= 1) {
      const value = ruleArray[idx];
      const currPosition = `${position}[${idx}]`;
      if (removeNextIndex) {
        ruleArray.splice(idx, 1);
        removeNextIndex = false;
      } else if (+value === thresholdId) {
        if (idx === ruleArray.length - 1) {
          removeNextIndex = true;
          ruleArray.splice(idx, 1);
        } else {
          ruleArray.splice(idx, 2);
        }
      } else if (Array.isArray(value)) {
        this.moveThreshold(currPosition, thresholdPosition, thresholdId, value);
        // Flatten single item arrays into parent threshold group
        if (value.length === 1) [ruleArray[idx]] = value;
      }

      if (currPosition === thresholdPosition) {
        ruleArray.splice(idx, 0, thresholdId, AND_OPERATOR);
      }
    }

    return this;
  }


  updateValue(position, value) {
    const [group, valuePosition] = this.getGroupAndValuePosition(position);
    group[valuePosition] = value;
    return this;
  }


  groupAtPosition(position) {
    const [group, valuePosition] = this.getGroupAndValuePosition(position);
    group.splice(
      valuePosition - 1, 3,
      [group[valuePosition - 1], group[valuePosition], group[valuePosition + 1]],
    );
    return this;
  }


  ungroupAtPosition(position) {
    const [group, valuePosition] = this.getGroupAndValuePosition(position);
    group.splice(valuePosition, 1, ...group[valuePosition]);
    return this;
  }


  splitAtPosition(groupPosition, position) {
    const [parentGroup, groupValuePosition] = this.getGroupAndValuePosition(groupPosition);
    const [group, valuePosition] = this.getGroupAndValuePosition(position);
    const newGroup = [
      group.slice(0, valuePosition), group[valuePosition], group.slice(valuePosition + 1),
    ];

    // Reduce single item arrays to single item
    newGroup[0] = newGroup[0].length === 1 ? newGroup[0][0] : newGroup[0];
    newGroup[2] = newGroup[2].length === 1 ? newGroup[2][0] : newGroup[2];

    // Splice new group into the parent group
    parentGroup.splice(groupValuePosition, 1, ...newGroup);

    return this;
  }


  getThresholdIds() {
    const idsInRule = this.toString().match(/\d+/g);
    return idsInRule ? idsInRule.map(id => +id) : [];
  }


  getTopLevelOperators() {
    return this.rule.filter(value => (
      value === AND_OPERATOR || value === OR_OPERATOR
    ));
  }


  getGroupAndValuePosition(position) {
    const [groupPosition, valuePosition] = splitPosition(position);
    const group = groupPosition === '[0]' ? this.rule : get(this.rule, groupPosition.slice(3));
    return [group, valuePosition];
  }


  toArray() {
    return this.rule;
  }


  toString() {
    return toString(this.rule);
  }
}

Logician.toArray = toArray;
Logician.toString = toString;
Logician.splitPosition = splitPosition;

export default Logician;
